import React from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@digital-twins/design-system/components/ui/tooltip'
import { personaThumbUrl } from '@/utils/personaThumbUrl'
import { FitText } from '../components/text/FitText'
import { glossedQuote, glossedQuoteText } from '../plots/quote-list/glossedQuote'
import { TextClamp } from '../components/text/TextClamp'
import { schemaBackedProseClampProps } from '../foundations/text-overflow-policy'
import { parseInlineBold } from '../primitives'
import { SlideFrame } from '../layouts/SlideFrame'
import { SlideHeaderChrome } from '../layouts/SlideHeaderChrome'
import { presetSlot } from '../layouts/presetSlots'
import { EditableText } from './EditableText'
import type { EditableSlideProps } from './contracts'
import type {
  QualAnalysisEditorialData,
  QualAnalysisEditorialPreview,
  QualAnalysisEditorialScope,
  QualAnalysisQuote,
  QualAnalysisRespondent,
  QualAnalysisSlideData,
} from './renderer-internal-types'
import { QualCreativeFilter, type QualCreativeFilterOption } from './QualCreativeFilter'

const QUAL = 'qual-analysis' as const
const QUOTE_BASE_FONT_SIZE = 38
const QUOTE_MIN_FONT_SIZE = 25
const QUOTE_LINE_HEIGHT = 1.22
const QUOTE_MAX_LINES = 7
const QUOTE_TWO_LINE_MIN_HEIGHT = 2 * QUOTE_BASE_FONT_SIZE * QUOTE_LINE_HEIGHT
const FEATURED_PORTRAIT_SIZE = 280
const FEATURED_QUOTE_WIDTH = 530
const FEATURED_COLUMN_GAP = 30
const FEATURED_BLOCK_WIDTH = FEATURED_PORTRAIT_SIZE + FEATURED_COLUMN_GAP + FEATURED_QUOTE_WIDTH

/**
 * The intro's measured box (editorial layout `narrative` slot, generated into
 * slide-layouts.json): the writer is judged against these lines and this width, so
 * the renderer draws exactly them and text that fits the box is never clamped.
 */
const INTRO_BOX = presetSlot(QUAL, 'narrative').text!
const EDITORIAL_INTRO = {
  ...schemaBackedProseClampProps('editorialIntroduction', QUAL, {
    lineHeight: 'var(--ds-lh-relaxed)',
  }),
  maxLines: INTRO_BOX.maxLines,
}
const EDITORIAL_BLURB = schemaBackedProseClampProps('editorialFeaturedBlurb', QUAL, {
  lineHeight: 'var(--ds-lh-normal)',
})
const EDITORIAL_THEME_TITLE = schemaBackedProseClampProps('editorialThemeTitle', QUAL, {
  lineHeight: 'var(--ds-lh-snug)',
})
/**
 * Theme titles are section labels, not display headlines. An `h2` inherits
 * `--font-display` (Sumana on Fairgen), and that face is never set bold.
 * `--ds-font-sans` is the theme body face: Geist on Fairgen, and the active
 * body face on every other theme.
 */
const THEME_TITLE_STYLE = {
  margin: 0,
  flexShrink: 0,
  fontFamily: 'var(--ds-font-sans)',
  fontSize: 18,
  fontWeight: 'var(--ds-weight-medium)' as any,
  color: 'var(--slide-text-primary)',
}
const EDITORIAL_THEME_TEXT = schemaBackedProseClampProps('editorialThemeText', QUAL, {
  lineHeight: 'var(--ds-lh-relaxed)',
})
const EDITORIAL_PREVIEW = schemaBackedProseClampProps('editorialPreviewText', QUAL, {
  lineHeight: 'var(--ds-lh-normal)',
})

/**
 * Vertical budget of the editorial rail (docs/plans/2026-09-18-qual-editorial-rail-overflow-plan.md).
 *
 * Shared chrome (53px = 14 + 28 + 10 + 1) sits outside the body; body bottom pad is 34px,
 * so content height is `787 - 53 - 34 = 700`. The rail gets `700 - 204.6 = 495.4px`
 * (content height minus h1 113 + intro 65.6 + body margin 26). Inner width is 1320
 * (1400 − 2×40); featured block 840; gap 45; rail width `1320 − 840 − 45 = 435`.
 * Each theme therefore owns `(495.4 - gap - paddingTop) / 2`. No notice renders below
 * the body: omitted evidence and low confidence are `diagnostics`, which no reader
 * component shows (P1), so the rail keeps its full height.
 */
const THEME_PADDING_BOTTOM = 18
const THEMES_GAP = 20

type EditorialLabels = {
  allCreatives: string
  triggerAriaLabel: string
  title: string
  explanation: string
  filterByAsset: string
  prevalence: string
  base: string
  aggregateScope: string
  reviewedAsset: string
  noEligibleAnswers: string
  noScopeThemes: string
  evidenceUnavailable: string
  overflow: string
}

export interface QualEditorialSlideProps extends QualAnalysisSlideData, EditableSlideProps {
  /** Controlled scope (`all` or `asset:<stable id>`); defaults to the aggregate scope. */
  selectedScopeId?: string
  /** Called when the slide-local creative selector changes scope. */
  onScopeChange?: (scopeId: string) => void
}

type EditorialThemeAnalysis = NonNullable<QualAnalysisEditorialData['themeAnalyses']>[number]

function clean(value?: string | null): string {
  return (value || '').replace(/\s+/g, ' ').trim()
}

function formatTemplate(template: string | undefined, values: Record<string, string | number>): string {
  const source = clean(template)
  if (!source) return ''
  return source.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? `{${key}}`))
}

function labelsFor(editorial?: QualAnalysisEditorialData | null, provided?: EditableSlideProps['qualCreativeFilterLabels']): EditorialLabels {
  return {
    allCreatives: provided?.allCreatives || '',
    triggerAriaLabel: provided?.triggerAriaLabel || '',
    title: provided?.title || '',
    explanation: provided?.explanation || '',
    filterByAsset: provided?.filterByAsset || '',
    prevalence: '',
    base: '',
    aggregateScope: '',
    reviewedAsset: '',
    noEligibleAnswers: '',
    noScopeThemes: '',
    evidenceUnavailable: '',
    overflow: '',
    ...editorial?.labels,
  }
}

function initials(respondent?: QualAnalysisRespondent | null): string {
  const name = clean(respondent?.name || respondent?.label)
  if (!name) return '?'
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
}

/**
 * The featured verbatim in quotation marks, as measured; and, when it carries
 * glossed runs, as drawn (each gloss in its muted span).
 */
function featuredQuoteRun(quote: QualAnalysisQuote): { measured: string; drawn?: React.ReactNode } {
  const text = clean(quote.text)
  return {
    measured: `“${glossedQuoteText(text, quote.textRuns)}”`,
    ...(quote.textRuns?.length ? { drawn: <>“{glossedQuote(text, quote.textRuns)}”</> } : {}),
  }
}

interface EditorialPortraitProps {
  respondent?: QualAnalysisRespondent | null
  size: number
  testId?: string
  staticMode?: boolean
  preview?: QualAnalysisEditorialPreview
}

function portraitThumbSize(renderedPx: number): 64 | 128 | 256 | 768 {
  if (renderedPx <= 64) {
    return 64
  }
  if (renderedPx <= 160) {
    return 128
  }
  return 768
}

/**
 * Ordered source candidates for one portrait, best first.
 *
 * The featured portrait renders far above the largest small-tile variant, so it
 * asks for `-t768.webp` and degrades to `-t256.webp` (the widest backfilled
 * variant today) before the full-size original — a persona without the newer
 * thumbnail therefore keeps exactly today's behavior instead of pulling ~2 MB.
 * Small tiles keep their single thumbnail -> original chain.
 *
 * A url `personaThumbUrl` does not rewrite (another host, not a persona PNG)
 * collapses to a single candidate after the dedupe, so a broken image reports
 * one error and never retries the same src.
 */
function portraitSourceCandidates(imageUrl: string, size: number): Array<string> {
  if (!imageUrl) {
    return []
  }
  const thumbSizes: Array<64 | 128 | 256 | 768> = size > 160 ? [768, 256] : [portraitThumbSize(size)]
  const candidates = [...thumbSizes.map((thumbSize) => personaThumbUrl(imageUrl, thumbSize) || imageUrl), imageUrl]
  return candidates.filter((url, index) => candidates.indexOf(url) === index)
}

function Portrait(props: EditorialPortraitProps) {
  const { respondent, size, testId, staticMode = false, preview } = props

  const imageUrl = clean(respondent?.imageUrl)
  const candidates = React.useMemo(() => portraitSourceCandidates(imageUrl, size), [imageUrl, size])
  const [sourceIndex, setSourceIndex] = React.useState(0)
  React.useEffect(() => {
    setSourceIndex(0)
  }, [imageUrl, size])
  const src = candidates[Math.min(sourceIndex, Math.max(0, candidates.length - 1))] || ''
  const [previewOpen, setPreviewOpen] = React.useState(false)
  const answerText = clean(preview?.answerText)
  const previewName = clean(preview?.respondent?.name || preview?.respondent?.label || respondent?.name || respondent?.label)
  const previewProfile = clean(preview?.respondent?.description || preview?.respondent?.profileSummary)
  const content = src ? (
    <img
      src={src}
      alt=""
      loading={size > 100 ? 'eager' : 'lazy'}
      onError={() => {
        setSourceIndex((index) => (index + 1 < candidates.length ? index + 1 : index))
      }}
      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 35%', filter: 'grayscale(1)', display: 'block' }}
    />
  ) : (
    <span aria-hidden="true" style={{ fontFamily: 'var(--ds-font-display)', fontSize: size > 100 ? 42 : 12, color: 'var(--slide-text-secondary)' }}>
      {initials(respondent)}
    </span>
  )

  const style: React.CSSProperties = {
    width: size,
    height: size,
    overflow: 'hidden',
    background: 'var(--slide-card-bg)',
    border: size > 100 ? '1px solid var(--slide-border-soft)' : '2px solid var(--slide-bg)',
    borderRadius: size > 100 ? 0 : '50%',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  }

  if (staticMode || !answerText) {
    return <div data-testid={testId} style={style}>{content}</div>
  }

  return (
    <TooltipProvider>
      <Tooltip open={previewOpen} onOpenChange={setPreviewOpen} delayDuration={300}>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            aria-label={previewName || answerText}
            aria-expanded={previewOpen}
            onPointerDown={(event) => {
              if (event.pointerType !== 'mouse') {
                event.preventDefault()
                setPreviewOpen((open) => !open)
              }
            }}
            style={{ ...style, padding: 0, cursor: 'default' }}
          >
            {content}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="text-xs"
          data-testid={testId ? `${testId}-preview` : undefined}
          style={{ maxWidth: 280, fontSize: 12 }}
        >
          <TextClamp minLines={1} maxLines={5} lineHeight={EDITORIAL_PREVIEW.lineHeight} style={{ fontSize: 12 }}>
            {answerText}
          </TextClamp>
          {previewName ? (
            <div style={{ marginTop: 4, fontSize: 12 }}>{previewName}</div>
          ) : null}
          {previewProfile ? (
            <TextClamp minLines={1} maxLines={2} lineHeight={EDITORIAL_PREVIEW.lineHeight} style={{ marginTop: 2, fontSize: 11, color: 'var(--slide-text-muted)' }}>
              {previewProfile}
            </TextClamp>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function findScope(
  themeScopes: Array<{ themeId: string; scopes: QualAnalysisEditorialScope[] }>,
  themeId: string,
  selectedScopeId: string,
): QualAnalysisEditorialScope | null {
  const entry = themeScopes.find((item) => item.themeId === themeId)
  if (!entry || !Array.isArray(entry.scopes)) return null
  const scope = entry.scopes.find((scope) => scope.scopeId === selectedScopeId)
  // Stored editorial payloads predate the builder's scoped integrity policy.
  // An invalid statistic becomes unavailable within this design, never a template switch.
  if (!scope || !Number.isInteger(scope.answerCount) || !Number.isInteger(scope.answerBase)
    || scope.answerCount < 0 || scope.answerBase < scope.answerCount) return null
  return scope
}

function deriveCreativeOptions(editorial: QualAnalysisEditorialData): QualCreativeFilterOption[] {
  const options: QualCreativeFilterOption[] = []
  const seen = new Set<string>()
  for (const theme of editorial.themeScopes || []) {
    for (const scope of theme.scopes || []) {
      if (!scope.scopeId.startsWith('asset:') || !scope.assetId || seen.has(scope.assetId)) continue
      seen.add(scope.assetId)
      options.push({
        scopeId: scope.scopeId,
        assetId: scope.assetId,
        label: clean(scope.assetLabel) || scope.assetId,
      })
    }
  }
  return options
}

function scopesMatch(left: QualAnalysisEditorialScope | null, right: QualAnalysisEditorialScope | null): boolean {
  if (!left || !right) {
    return false
  }
  return left.answerCount === right.answerCount && left.answerBase === right.answerBase
}

function aggregateScopeLabel(
  creativeOptions: Array<QualCreativeFilterOption>,
  labels: EditorialLabels,
  aggregate: QualAnalysisEditorialScope | null,
  soleCreative: QualAnalysisEditorialScope | null,
): string {
  if (creativeOptions.length >= 2) {
    return labels.aggregateScope
  }
  if (creativeOptions.length === 1 && scopesMatch(aggregate, soleCreative)) {
    return creativeOptions[0].label
  }
  return ''
}

function scopeAttribution(
  scope: QualAnalysisEditorialScope | null,
  creativeOptions: Array<QualCreativeFilterOption>,
  labels: EditorialLabels,
  aggregate: QualAnalysisEditorialScope | null,
  soleCreative: QualAnalysisEditorialScope | null,
): string {
  if (!scope) {
    return ''
  }
  if (scope.scopeId === 'all') {
    return aggregateScopeLabel(creativeOptions, labels, aggregate, soleCreative)
  }
  return clean(scope.assetLabel)
}

function isAggregateAnalysis(analysis: EditorialThemeAnalysis): boolean {
  return analysis.scopeId == null || analysis.scopeId === 'all'
}

interface EditorialThemeSelection {
  id: string
  title: string
  text: string
  quoteAssetId: string
  quoteAssetLabel: string
  analysisIndex: number
}

function selectAggregateEditorialThemes(
  analyses: Array<EditorialThemeAnalysis>,
): Array<EditorialThemeSelection> {
  const selected: Array<EditorialThemeSelection> = []
  for (let analysisIndex = 0; analysisIndex < analyses.length; analysisIndex += 1) {
    if (selected.length >= 2) {
      break
    }
    const analysis = analyses[analysisIndex]
    if (!isAggregateAnalysis(analysis)) {
      continue
    }
    const title = clean(analysis.title)
    const text = clean(analysis.text)
    if (!title || !text) {
      continue
    }
    selected.push({
      id: analysis.themeId,
      title,
      text,
      quoteAssetId: clean(analysis.quoteAssetId),
      quoteAssetLabel: clean(analysis.quoteAssetLabel),
      analysisIndex,
    })
  }
  return selected
}

function selectEditorialThemes(
  themeScopes: Array<{ themeId: string }>,
  analyses: Array<EditorialThemeAnalysis>,
  selectedId: string,
  scopeThemes?: Array<{ scopeId: string; themeIds: Array<string> }>,
): Array<EditorialThemeSelection> {
  if (!Array.isArray(scopeThemes) || scopeThemes.length === 0) {
    return themeScopes
      .map((entry) => {
        const analysisIndex = analyses.findIndex((item: EditorialThemeAnalysis) => item.themeId === entry.themeId)
        const analysis = analysisIndex >= 0 ? analyses[analysisIndex] : undefined
        return {
          id: entry.themeId,
          title: clean(analysis?.title),
          text: clean(analysis?.text),
          quoteAssetId: clean(analysis?.quoteAssetId),
          quoteAssetLabel: clean(analysis?.quoteAssetLabel),
          analysisIndex,
        }
      })
      .filter((theme) => Boolean(theme.title && theme.text))
      .slice(0, 2)
  }

  if (selectedId === 'all') {
    return selectAggregateEditorialThemes(analyses)
  }

  const scopeEntry = scopeThemes.find((entry) => entry.scopeId === selectedId)
  if (!scopeEntry) {
    return selectAggregateEditorialThemes(analyses)
  }

  return scopeEntry.themeIds
    .map((themeId) => {
      const analysisIndex = analyses.findIndex(
        (item: EditorialThemeAnalysis) => item.themeId === themeId && item.scopeId === selectedId,
      )
      const analysis = analysisIndex >= 0 ? analyses[analysisIndex] : undefined
      return {
        id: themeId,
        title: clean(analysis?.title),
        text: clean(analysis?.text),
        quoteAssetId: clean(analysis?.quoteAssetId),
        quoteAssetLabel: clean(analysis?.quoteAssetLabel),
        analysisIndex,
      }
    })
    .filter((theme) => Boolean(theme.title && theme.text))
}

function resolveDisplayedFeatured(
  featuredQuote: QualAnalysisSlideData['featuredQuote'],
  editorial: QualAnalysisEditorialData | null,
  selectedId: string,
): { quote: QualAnalysisQuote; editorial: QualAnalysisEditorialData; blurbField: string } | null {
  if (!editorial) {
    return null
  }
  const people = editorial.scopeFeatured
  if (selectedId !== 'all' && Array.isArray(people)) {
    const index = people.findIndex((person) => person.scopeId === selectedId)
    const person = index >= 0 ? people[index] : undefined
    const text = clean(person?.text)
    if (!person || !text) {
      return null
    }
    return {
      quote: {
        text,
        ...(person.textRuns?.length ? { textRuns: person.textRuns } : {}),
        respondent: person.respondent,
      },
      editorial: {
        ...editorial,
        featuredBlurb: clean(person.blurb),
        featuredAssetId: clean(person.assetId),
      },
      blurbField: `editorial.scopeFeatured.${index}.blurb`,
    }
  }
  const text = clean(featuredQuote?.text)
  if (!featuredQuote || !text) {
    return null
  }
  return {
    quote: featuredQuote,
    editorial,
    blurbField: 'editorial.featuredBlurb',
  }
}

function FeaturedPortrait({ quote }: { quote: QualAnalysisQuote }) {
  return <Portrait respondent={quote.respondent} size={FEATURED_PORTRAIT_SIZE} testId="qual-editorial-featured-portrait" />
}

type FeaturedMetaPlacement = 'quote' | 'portrait'

function FeaturedMeta({
  quote,
  editorial,
  labels,
  editable,
  onEdit,
  placement,
  blurbField,
}: {
  quote: QualAnalysisQuote
  editorial: QualAnalysisEditorialData
  labels: EditorialLabels
  editable: boolean
  onEdit?: (field: string, value: string) => void
  placement: FeaturedMetaPlacement
  blurbField: string
}) {
  const respondentName = clean(quote.respondent?.name || quote.respondent?.label)
  const blurb = clean(editorial.featuredBlurb)
  const featuredAsset = clean(editorial.featuredAssetId)
  const scopedLabel = featuredAsset
    ? clean(
        editorial.themeScopes
          .flatMap((theme) => theme.scopes || [])
          .find((scope) => scope.assetId === featuredAsset)?.assetLabel,
      )
    : ''
  const assetLabel = scopedLabel || clean(quote.variantLabel) || featuredAsset
  const underQuote = placement === 'quote'

  return (
    <div data-testid="qual-featured-meta" data-placement={placement} style={{ minWidth: 0, marginTop: underQuote ? 22 : 14 }}>
      {respondentName ? (
        <TextClamp minLines={1} maxLines={1} lineHeight="1.4" style={{ fontSize: 18, fontWeight: 'var(--ds-weight-semibold)' as any, color: 'var(--slide-text-primary)' }}>
          {respondentName}
        </TextClamp>
      ) : null}
      {blurb ? (
        editable && onEdit ? (
          <EditableText
            value={blurb}
            field={blurbField}
            editable
            onEdit={onEdit}
            tag="p"
            multiline
            style={{ marginTop: 8, fontSize: 15, color: 'var(--slide-text-secondary)' }}
          />
        ) : (
          <TextClamp minLines={2} maxLines={underQuote ? 3 : 4} lineHeight={EDITORIAL_BLURB.lineHeight} style={{ marginTop: 8, fontSize: 15, color: 'var(--slide-text-secondary)' }}>
            {parseInlineBold(blurb)}
          </TextClamp>
        )
      ) : null}
      {assetLabel ? (
        <span
          data-testid="qual-featured-asset"
          style={{ display: 'inline-block', maxWidth: '100%', marginTop: 13, padding: '6px 10px', borderRadius: 5, background: 'var(--slide-card-bg)', color: 'var(--slide-text-muted)', fontSize: 11, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top', boxSizing: 'border-box' }}
        >
          {formatTemplate(labels.reviewedAsset, { asset: assetLabel })}
        </span>
      ) : null}
    </div>
  )
}

function FeaturedEvidence({
  quote,
  quoteRef,
  footer,
}: {
  quote: QualAnalysisQuote
  quoteRef?: React.Ref<HTMLDivElement>
  footer?: React.ReactNode
}) {
  return (
    <div data-testid="qual-featured-evidence" style={{ minWidth: 0, width: FEATURED_QUOTE_WIDTH, maxWidth: FEATURED_QUOTE_WIDTH }}>
      <div ref={quoteRef} style={{ minHeight: QUOTE_TWO_LINE_MIN_HEIGHT }}>
        <FitText
          as="blockquote"
          data-testid="qual-featured-quote"
          rendered={featuredQuoteRun(quote).drawn}
          maxLines={QUOTE_MAX_LINES}
          baseFontSize={QUOTE_BASE_FONT_SIZE}
          minFontSize={QUOTE_MIN_FONT_SIZE}
          lineHeight={QUOTE_LINE_HEIGHT}
          style={{
            margin: 0,
            padding: 0,
            border: 0,
            fontFamily: 'var(--ds-font-display)',
            letterSpacing: '-0.03em',
            color: 'var(--slide-text-primary)',
          }}
        >
          {featuredQuoteRun(quote).measured}
        </FitText>
      </div>
      {footer}
    </div>
  )
}

/**
 * Featured block: the 280px portrait column plus the 530px quote column, with the
 * respondent meta placed wherever it leaves the least dead space.
 *
 * Default is under the quote, where the blurb gets the full quote-column width
 * instead of the portrait's 280px. A quote taller than the portrait already fills
 * that column, so the meta goes back under the portrait and balances the block.
 *
 * The quote column is exactly the same width in both placements, so the measured
 * quote height never depends on where the meta ends up: the decision cannot
 * oscillate and needs no hysteresis. FitText re-fits after webfonts load, so a
 * one-shot measure would read the fallback-font height; the ResizeObserver picks
 * up the re-fit (and any container resize) instead.
 */
function FeaturedBlock({
  featuredQuote,
  editorial,
  labels,
  editable,
  onEdit,
  blurbField,
}: {
  featuredQuote: QualAnalysisSlideData['featuredQuote']
  editorial: QualAnalysisEditorialData | null
  labels: EditorialLabels
  editable: boolean
  onEdit?: (field: string, value: string) => void
  blurbField: string
}) {
  const quoteRef = React.useRef<HTMLDivElement | null>(null)
  const [quoteHeight, setQuoteHeight] = React.useState(0)
  const quoteText = clean(featuredQuote?.text)

  React.useLayoutEffect(() => {
    const node = quoteRef.current
    if (!node) {
      setQuoteHeight(0)
      return undefined
    }
    const measure = () => {
      // offsetHeight is canvas px; getBoundingClientRect would shrink with the deck's fit-to-viewport scale.
      setQuoteHeight(node.offsetHeight)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [quoteText])

  const placement: FeaturedMetaPlacement = quoteHeight > FEATURED_PORTRAIT_SIZE ? 'portrait' : 'quote'
  const meta = featuredQuote && editorial ? (
    <FeaturedMeta quote={featuredQuote} editorial={editorial} labels={labels} editable={editable} onEdit={onEdit} placement={placement} blurbField={blurbField} />
  ) : null

  return (
    <section data-testid="qual-editorial-featured" style={{ display: 'grid', gridTemplateColumns: `${FEATURED_PORTRAIT_SIZE}px ${FEATURED_QUOTE_WIDTH}px`, gap: FEATURED_COLUMN_GAP, alignItems: 'start', minWidth: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column', width: FEATURED_PORTRAIT_SIZE, maxWidth: FEATURED_PORTRAIT_SIZE, minWidth: 0 }}>
        {featuredQuote ? <FeaturedPortrait quote={featuredQuote} /> : null}
        {placement === 'portrait' ? meta : null}
      </div>
      {featuredQuote && editorial ? (
        <FeaturedEvidence quote={featuredQuote} quoteRef={quoteRef} footer={placement === 'quote' ? meta : null} />
      ) : null}
    </section>
  )
}

/** Legacy: a stored scope without its stamped `percent` prints today's figure. */
function legacyPrevalencePercent(scope: QualAnalysisEditorialScope): number {
  return Math.round((Math.max(0, scope.answerCount) / scope.answerBase) * 100)
}

function prevalenceText(scope: QualAnalysisEditorialScope | null, labels: EditorialLabels): { primary: string; base: string } {
  if (!scope || !Number.isFinite(scope.answerBase)) {
    return { primary: labels.evidenceUnavailable, base: '' }
  }
  if (scope.answerBase === 0) {
    return { primary: labels.noEligibleAnswers, base: '' }
  }
  // Python stamps the printed percent (plots wave 7, D6); a scope stored
  // before the stamp keeps today's computation (legacy reader only).
  const percent = typeof scope.percent === 'number' ? scope.percent : legacyPrevalencePercent(scope)
  return {
    primary: formatTemplate(labels.prevalence, { percent }),
    base: formatTemplate(labels.base, { count: Math.max(0, scope.answerCount), base: scope.answerBase }),
  }
}

function ThemeEvidence({
  themeId,
  title,
  text,
  scope,
  labels,
  staticMode,
  editable,
  onEdit,
  themeIndex,
  quoteAssetId,
  quoteAssetLabel,
  creativeOptions,
  aggregate,
  soleCreative,
}: {
  themeId: string
  title: string
  text: string
  scope: QualAnalysisEditorialScope | null
  labels: EditorialLabels
  staticMode: boolean
  editable: boolean
  onEdit?: (field: string, value: string) => void
  themeIndex: number
  quoteAssetId: string
  quoteAssetLabel: string
  creativeOptions: Array<QualCreativeFilterOption>
  aggregate: QualAnalysisEditorialScope | null
  soleCreative: QualAnalysisEditorialScope | null
}) {
  // The producer's previews, as sent: distinct twins and at most four are
  // Python's contract (`_validate_editorial_evidence`).
  const previews = scope?.previews ?? []
  const { primary, base } = prevalenceText(scope, labels)
  const hasKnownMembership = scope?.identityComplete === true && Number.isFinite(scope.distinctTwinCount)
  const overflow = hasKnownMembership ? Math.max(0, Number(scope?.distinctTwinCount) - previews.length) : 0
  const assetLabel = scopeAttribution(scope, creativeOptions, labels, aggregate, soleCreative)
  const quoteAssetAttribution = quoteAssetLabel && quoteAssetId !== scope?.assetId && !(
    creativeOptions.length === 1 && creativeOptions[0].assetId === quoteAssetId
  )
    ? formatTemplate(labels.reviewedAsset, { asset: quoteAssetLabel })
    : ''
  const baseScope = assetLabel ? `${base}${base ? ' · ' : ''}${assetLabel}` : base

  return (
    <article
      data-testid={`qual-theme-${themeId}`}
      style={{
        // An explicit flex column, not a block: `flex: 1` on the prose below is inert in a
        // block, and `overflow: hidden` on a block clips the LAST child -- the avatar row
        // and the prevalence line -- instead of the prose. In a column with a shrinkable
        // prose child and `flexShrink: 0` on the title and the evidence row, prose is the
        // only element that can yield, so any residual clip lands in the paragraph.
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        paddingBottom: THEME_PADDING_BOTTOM,
        borderBottom: '1px solid var(--slide-border-soft)',
      }}
    >
      {editable && onEdit ? (
        <EditableText
          value={title}
          field={`editorial.themeAnalyses.${themeIndex}.title`}
          editable
          onEdit={onEdit}
          tag="h2"
          style={THEME_TITLE_STYLE}
        />
      ) : (
        <TextClamp
          as="h2"
          data-testid={`qual-theme-title-${themeId}`}
          minLines={EDITORIAL_THEME_TITLE.minLines}
          maxLines={EDITORIAL_THEME_TITLE.maxLines}
          lineHeight={EDITORIAL_THEME_TITLE.lineHeight}
          style={THEME_TITLE_STYLE}
        >
          {title}
        </TextClamp>
      )}
      {editable && onEdit ? (
        <EditableText
          value={text}
          field={`editorial.themeAnalyses.${themeIndex}.text`}
          editable
          onEdit={onEdit}
          tag="p"
          multiline
          style={{ margin: '13px 0 15px', fontSize: 17, color: 'var(--slide-text-secondary)' }}
        />
      ) : (
        <TextClamp
          as="p"
          data-testid={`qual-theme-text-${themeId}`}
          minLines={EDITORIAL_THEME_TEXT.minLines}
          maxLines={EDITORIAL_THEME_TEXT.maxLines}
          fitLines
          tooltipOnTruncate
          // The clamped children are `parseInlineBold(text)`, an array, so
          // `TextClamp` cannot derive a native title from them. Without an explicit
          // one the static export -- which renders no Radix trigger, by design --
          // carries no full text at all, and a clipped paragraph in a PDF would be
          // silently short. Interactive still suppresses the native title so the
          // Radix tooltip is not doubled.
          title={text}
          renderMode={staticMode ? 'static' : 'interactive'}
          lineHeight={EDITORIAL_THEME_TEXT.lineHeight}
          style={{
            margin: '13px 0 15px',
            fontSize: 17,
            color: 'var(--slide-text-secondary)',
            // Prose yields, and only prose — by LINE COUNT, not pixels. `minHeight: 0`
            // overrides the `minLines` reserve `TextClamp` writes (its own `style`
            // spread wins), because a `min-height` beats `flex-shrink` and would push
            // the shortfall onto the evidence row below.
            //
            // `flex: 1 1 auto` lets the algorithm assign leftover height; `fitLines`
            // then quantizes that allotment to a whole number of lines so
            // `overflow: hidden` never cuts a glyph mid-letter. Growth past the
            // fitted `maxHeight` is a no-op, so a short paragraph does not stretch
            // and float the evidence row off its prose.
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          {parseInlineBold(text)}
        </TextClamp>
      )}
      {quoteAssetAttribution ? (
        <div
          data-testid={`qual-theme-quote-asset-${themeId}`}
          style={{ marginBottom: 10, flexShrink: 0, fontSize: 12, color: 'var(--slide-text-muted)' }}
        >
          {quoteAssetAttribution}
        </div>
      ) : null}
      {/*
        Budgeted at its WRAPPED height (~55px: a 14px primary line over a 12px/1.45 base
        line whose `baseScope` wraps beside the avatars), not at the 35px `minHeight`
        floor. `flexShrink: 0` is what keeps the avatars and the prevalence line whole
        when the rail is oversubscribed.
      */}
      <div data-testid={`qual-theme-evidence-${themeId}`} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 35, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 0 }}>
          {previews.map((preview, index) => (
            <div
              // Keyed by answerId, not respondentId: hand-crafted previews can
              // repeat a respondent (the same twin quoted under two scopes), and
              // a respondentId key would collide and drop one from the DOM.
              // Falls back to respondentId+index only when answerId is missing.
              key={preview.answerId || `${preview.respondentId}-${index}`}
              style={{ marginLeft: index === 0 ? 0 : -8, zIndex: previews.length - index }}
            >
              <Portrait
                respondent={preview.respondent}
                size={33}
                preview={preview}
                staticMode={staticMode}
                testId={`qual-theme-avatar-${themeId}-${preview.respondentId}`}
              />
            </div>
          ))}
          {overflow > 0 ? (
            <span
              data-testid={`qual-theme-overflow-${themeId}`}
              aria-label={formatTemplate(labels.overflow, { count: overflow })}
              style={{ width: 36, height: 33, marginLeft: -8, border: '2px solid var(--slide-bg)', borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'var(--slide-card-bg)', color: 'var(--slide-text-muted)', fontSize: 10, zIndex: 1 }}
            >
              +{overflow}
            </span>
          ) : null}
        </div>
        <div data-testid={`qual-theme-prevalence-${themeId}`} style={{ minWidth: 0, color: 'var(--slide-text-muted)', fontSize: 12, lineHeight: 1.45 }}>
          <div style={{ color: 'var(--slide-highlight)', fontSize: 14, fontWeight: 'var(--ds-weight-semibold)' as any }}>{primary}</div>
          {baseScope ? <div>{baseScope}</div> : null}
        </div>
      </div>
    </article>
  )
}

export function QualEditorialSlide(props: QualEditorialSlideProps) {
  const {
    editorial,
    featuredQuote,
    eyebrow,
    title,
    actionTitle,
    clientLogoUrl,
    selectedScopeId,
    onScopeChange,
    editable = false,
    onEdit,
    renderMode = 'interactive',
    qualCreativeFilterLabels,
  } = props

  const safeEditorial = editorial || null
  const labels = labelsFor(safeEditorial, qualCreativeFilterLabels)
  const options = safeEditorial ? deriveCreativeOptions(safeEditorial) : []
  const [localScopeId, setLocalScopeId] = React.useState(selectedScopeId || 'all')
  React.useEffect(() => {
    if (selectedScopeId) setLocalScopeId(selectedScopeId)
  }, [selectedScopeId])
  const isStatic = renderMode === 'static'
  const effectiveScopeId = isStatic ? 'all' : (selectedScopeId || localScopeId)
  const selectedId = effectiveScopeId === 'all' || options.some((option) => option.scopeId === effectiveScopeId)
    ? effectiveScopeId
    : 'all'
  const handleScopeChange = (scopeId: string) => {
    setLocalScopeId(scopeId)
    onScopeChange?.(scopeId)
  }
  const headline = clean(actionTitle) || clean(title)
  const intro = clean(safeEditorial?.introduction)
  const analyses = safeEditorial?.themeAnalyses || []
  const selectedThemes = selectEditorialThemes(
    safeEditorial?.themeScopes || [],
    analyses,
    selectedId,
    safeEditorial?.scopeThemes,
  )
  const listedScope = (safeEditorial?.scopeThemes || []).find((entry) => entry.scopeId === selectedId)
  const hasEmptyListedScope = selectedId !== 'all' && Boolean(listedScope && listedScope.themeIds.length === 0)
  const displayedFeatured = resolveDisplayedFeatured(featuredQuote, safeEditorial, selectedId)
  const hasFeaturedQuote = Boolean(displayedFeatured)

  const filterNode = safeEditorial && !isStatic && options.length > 1 ? (
    <QualCreativeFilter
      options={options}
      selectedId={selectedId}
      onSelect={handleScopeChange}
      labels={{
        allCreatives: labels.allCreatives,
        triggerAriaLabel: labels.triggerAriaLabel,
        title: labels.title,
        explanation: labels.explanation,
        filterByAsset: labels.filterByAsset,
      }}
    />
  ) : undefined

  // The slide's base line (`exhibitBase`, "Based on 66 answers"), in the
  // frame's own footer, as every other exhibit prints it (RND-2954, RND-2956).
  const footerText = clean(props.footerText)

  return (
    <SlideFrame fullBleed background="var(--slide-bg)" footerText={footerText || undefined} editable={editable} onEdit={onEdit}>
      <SlideHeaderChrome
        eyebrowText={clean(eyebrow)}
        clientLogoUrl={clientLogoUrl}
        eyebrowTextTransform="none"
      />
      <div
        data-testid="qual-editorial-slide"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          // The frame's footer takes the bottom margin when the slide has one.
          padding: `0 var(--ds-slide-pad-x) ${filterNode ? 48 : footerText ? 12 : 34}px`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxSizing: 'border-box',
          color: 'var(--slide-text-primary)',
        }}
      >
        {editable && onEdit ? (
          <EditableText
            value={headline}
            field="actionTitle"
            editable
            onEdit={onEdit}
            tag="h1"
            style={{ margin: '17px 0 0', flexShrink: 0, maxWidth: 1160, fontFamily: 'var(--ds-font-display)', fontSize: 40, fontWeight: 'var(--ds-weight-regular)' as any, color: 'var(--slide-text-primary)' }}
          />
        ) : (
          <TextClamp as="h1" minLines={1} maxLines={2} lineHeight="1.2" style={{ margin: '17px 0 0', flexShrink: 0, maxWidth: 1160, fontFamily: 'var(--ds-font-display)', fontSize: 40, fontWeight: 'var(--ds-weight-regular)' as any, color: 'var(--slide-text-primary)' }}>
            {headline}
          </TextClamp>
        )}
        {intro ? (
          editable && onEdit ? (
            <EditableText
              value={intro}
              field="editorial.introduction"
              editable
              onEdit={onEdit}
              tag="p"
              multiline
              style={{ margin: '18px 0 0', flexShrink: 0, maxWidth: INTRO_BOX.width, fontSize: INTRO_BOX.fontSize, color: 'var(--slide-text-secondary)' }}
            />
          ) : (
            <TextClamp
              as="p"
              data-testid="qual-editorial-introduction"
              minLines={EDITORIAL_INTRO.minLines}
              maxLines={EDITORIAL_INTRO.maxLines}
              lineHeight={EDITORIAL_INTRO.lineHeight}
              style={{ margin: '18px 0 0', flexShrink: 0, maxWidth: INTRO_BOX.width, fontSize: INTRO_BOX.fontSize, color: 'var(--slide-text-secondary)' }}
            >
              {parseInlineBold(intro)}
            </TextClamp>
          )
        ) : null}
        <div data-testid="qual-editorial-body" style={{ display: 'grid', gridTemplateColumns: hasFeaturedQuote ? `${FEATURED_BLOCK_WIDTH}px minmax(0, 1fr)` : 'minmax(0, 1fr)', gap: 45, marginTop: 26, flex: '1 1 auto', minHeight: 0 }}>
          {displayedFeatured ? <FeaturedBlock
            featuredQuote={displayedFeatured.quote}
            editorial={displayedFeatured.editorial}
            labels={labels}
            editable={editable}
            onEdit={onEdit}
            blurbField={displayedFeatured.blurbField}
          /> : null}
          <section
            data-testid="qual-editorial-themes"
            style={{
              // Even rows, not an auto-height flex column: `minmax(0, 1fr)` hands each
              // theme exactly half the real rail, so an oversubscribed theme cannot push
              // the next one past the slide edge. Only applied once there is more than one
              // theme -- a single theme has no deficit and keeps its content height.
              display: 'grid',
              ...(selectedThemes.length > 1
                ? { gridTemplateRows: `repeat(${selectedThemes.length}, minmax(0, 1fr))` }
                : {}),
              alignContent: 'start',
              gap: THEMES_GAP,
              minWidth: 0,
              minHeight: 0,
              paddingTop: 2,
            }}
          >
            {selectedThemes.map((theme) => (
              <ThemeEvidence
                key={theme.id}
                themeId={theme.id}
                title={theme.title}
                text={theme.text}
                scope={safeEditorial ? findScope(safeEditorial.themeScopes, theme.id, selectedId) : null}
                labels={labels}
                staticMode={isStatic}
                editable={editable}
                onEdit={onEdit}
                themeIndex={theme.analysisIndex}
                quoteAssetId={theme.quoteAssetId}
                quoteAssetLabel={theme.quoteAssetLabel}
                creativeOptions={options}
                aggregate={safeEditorial ? findScope(safeEditorial.themeScopes, theme.id, 'all') : null}
                soleCreative={safeEditorial && options.length === 1 ? findScope(safeEditorial.themeScopes, theme.id, options[0].scopeId) : null}
              />
            ))}
            {!safeEditorial ? <>
              {(props.insightBlocks || []).map((block, index) => (
                <p key={index} data-testid="qual-editorial-unavailable">{block.text}</p>
              ))}
              {props.stats?.length ? <div data-testid="qual-editorial-unavailable-stats" style={{ fontSize: 14, color: 'var(--slide-text-muted)' }}>
                {props.stats.map((stat) => `${stat.value} ${stat.label}`).join(' · ')}
              </div> : null}
            </> : null}
            {hasEmptyListedScope ? (
              <div data-testid="qual-editorial-no-scope-themes" style={{ color: 'var(--slide-text-muted)', fontSize: 14 }}>{labels.noScopeThemes}</div>
            ) : null}
            {!hasEmptyListedScope && selectedThemes.length === 0 && safeEditorial ? (
              <div data-testid="qual-editorial-empty" style={{ color: 'var(--slide-text-muted)', fontSize: 14 }}>{labels.evidenceUnavailable}</div>
            ) : null}
          </section>
        </div>
      </div>
      {filterNode ? (
        <div
          style={{
            position: 'absolute',
            left: 'var(--ds-slide-pad-x)',
            // Above the frame's footer line when there is one.
            bottom: footerText ? 44 : 16,
            zIndex: 2,
          }}
        >
          {filterNode}
        </div>
      ) : null}
    </SlideFrame>
  )
}
